import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator, ValidationErrors } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, pairwise, shareReplay, startWith, switchMap } from 'rxjs/operators';

import { GearService } from '../gear.service';
import { orderByLatest } from '../gear.utils';
import { LoggingService } from '../../logging.service';
import { SelectOption, SelectOptionGroup } from '../../shared/form.utils';
import { Gear } from '../../shared/models/gear.model';
import { replayValue } from '../../shared/observable.utils';

export type SelectGearContext = 'analysis' | 'data';
export type ContextWithOption = SelectGearContext | 'option';
export interface GearVersionSelection {
  id: string
  name: string
  label: string
}

// to prevent FLYW-30397 issue: bazar case where some users find the gear suite with parenthesis
// appended to the end of the label in the autocomplete, causing validation to break
export function normalizeGearLabel(label: string, suite: string): string {
  const gearLabel = (label || '').trim();
  return suite && gearLabel.endsWith(`(${suite})`) ? gearLabel.replace(/\(.*\)$/, '') : gearLabel;
}
export function getNormalizedGearLabel(gear: Gear): string {
  return normalizeGearLabel(gear.gear?.label, gear.gear?.custom?.flywheel?.suite);
}

@Component({
  host: {
    'test-id': 'gear-version-selector',
    '(blur)': 'onTouched()',
  },
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    useExisting: forwardRef(() => GearVersionSelectorComponent),
    multi: true,
  }, {
    provide: NG_VALIDATORS,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    useExisting: forwardRef(() => GearVersionSelectorComponent),
    multi: true,
  }],
  selector: 'gear-version-selector',
  styleUrls: ['./gear-version-selector.component.scss'],
  templateUrl: './gear-version-selector.component.html',
})
export class GearVersionSelectorComponent implements ControlValueAccessor, OnChanges, OnDestroy, OnInit, Validator {
  @Input() billingLabels: Record<string, string> = {} // gear.gear.name -> label
  @Input() context: ContextWithOption = 'option'
  @Input() disableName = false
  @Input() disableVersion = false
  @Input() gears: Record<string, Gear> = {}
  @Input() isLoading = false

  @Output() contextChange = new EventEmitter<ContextWithOption>()

  filteredGearOptions$: Observable<SelectOptionGroup[]>
  form = new FormGroup({
    id: new FormControl(null),
    name: new FormControl(null),
    label: new FormControl(null),
  })
  isQuerying = false
  private refreshGearOptions$: Subject<void> = new Subject()
  shouldReset = true
  private subscriptions = {
    init: Subscription.EMPTY,
    gearsByLabel: Subscription.EMPTY,
  };
  versionOptions: SelectOption[] = []

  /* eslint-disable @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars */
  private onChange = (value: GearVersionSelection): void => {}
  private onTouched = (): void => {}
  /* eslint-enable @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars */
  constructor(
    private logger: LoggingService,
    private gearService: GearService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.init = combineLatest([
      replayValue(this.form.get('id')),
      replayValue(this.form.get('name')),
      replayValue(this.form.get('label')),
    ]).pipe(
      map(([id, name, label]) => ({ id, name, label })),
      distinctUntilChanged((prev, next) => prev.id === next.id && prev.name === next.name && prev.label === next.label),
      delay(0), // allow emit during writeValue
      pairwise(),
    ).subscribe(([prev, next]) => {
      this.onChange(next);
      if (prev.name !== next.name) {
        this.updateVersionOptions();
      }
    });
    this.filteredGearOptions$ = combineLatest([
      replayValue(this.form.get('label')).pipe(debounceTime(200), startWith('')),
      this.refreshGearOptions$.pipe(startWith(null)),
    ]).pipe(
      map(([gearNameFilter]) => {
        let gearVersions = Object.values(this.gears).filter(
          this.context === 'analysis'
            ? gear => gear.category === 'analysis'
            : gear => gear.category !== 'analysis'
        );
        if (this.context === 'option') {
          if (this.shouldReset) {
            this.shouldReset = false;
            this.clearSelectedGear();
          }
          return [];
        }
        this.shouldReset = true;
        gearVersions = gearVersions.filter(gear => {
          const gearLabel = getNormalizedGearLabel(gear).toLowerCase();
          const filter = normalizeGearLabel(gearNameFilter, gear.gear?.custom?.flywheel?.suite).toLowerCase();
          return gear.gear.name.toLowerCase().includes(filter) || gearLabel.includes(filter);
        });
        const gearSuites = groupBy(
          uniqBy(orderByLatest(gearVersions), 'gear.name'),
          gear => gear.gear?.custom?.flywheel?.suite || '',
        );
        const gearOptions = Object.entries(gearSuites).map(([suite, gears]) => ({
          label: suite,
          options: orderBy(
            gears.map(gear => ({ value: gear.gear?.name, label: getNormalizedGearLabel(gear) })),
            option => option.label.toLowerCase(),
            'asc',
          ),
        }));
        return orderBy(gearOptions, group => group.label?.toLowerCase(), 'asc');
      }),
      shareReplay(1),
    );
    this.filteredGearOptions$.subscribe();
    this.form.valueChanges.pipe(
      debounceTime(200),
      filter((values) => !Object.values(values).some(v => v === null) && !this.isQuerying),
      switchMap((values: GearVersionSelection) => {
        this.isQuerying = true;
        // must call to retrieve all the gear versions - only latest verions are initially loaded
        return this.gearService.getGearsByName(values.name);
      }),
    ).subscribe({
      next: (gears) => {
        this.isQuerying = false;
        for (const gear of gears) {
          this.gears[gear._id] = gear;
        }
        this.refreshGearOptions$.next();
        this.updateVersionOptions();
      },
      error: (error) => {
        this.logger.error(error);
        this.isQuerying = false;
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.context || changes.gears) {
      this.refreshGearOptions$.next();
    }
    if (changes.disableName) {
      // when input changes, to update reactive form we need to defer execution
      setTimeout(() => {
        this.form.get('name')[this.disableName ? 'disable' : 'enable']();
        this.form.get('label')[this.disableName ? 'disable' : 'enable']();
      });
    }
    if (changes.disableVersion) {
      this.updateVersionOptions();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get selectedGearId(): string {
    return this.form.get('id').value;
  }

  setContext(context: ContextWithOption): void {
    this.context = context;
    this.contextChange.emit(context);
    this.refreshGearOptions$.next();
  }

  private updateVersionOptions(): void {
    const gearName = this.form.get('name').value;
    this.versionOptions = orderByLatest(
      Object.values(this.gears).filter(gear => (
        gear.gear.name === gearName &&
        // if category changes between gear versions we need to filter by context
        (this.context === 'analysis' ? gear.category === 'analysis' : gear.category !== 'analysis')
      ))
    ).map(gear => ({ value: gear._id, label: gear.gear.version }));
    const gearId = this.form.get('id');
    if (this.versionOptions.length > 0 && !this.disableVersion) {
      gearId.enable({ emitEvent: false });
    } else {
      gearId.disable({ emitEvent: false });
    }
    if (!this.versionOptions.some(version => version.value === gearId.value)) {
      this.selectLatestVersion();
    }
  }

  selectLatestVersion(): void {
    // only change the version if we have a valid replacement
    if (this.versionOptions[0]?.value){
      this.form.get('id').setValue(this.versionOptions[0]?.value);
    }
  }

  selectGearOption(event: MatAutocompleteSelectedEvent): void {
    const selectedGear = Object.values(this.gears).find(gear => gear.gear.name === event.option.value);
    this.form.setValue({
      id: selectedGear._id,
      name: event.option.value,
      label: event.option.getLabel(),
    });
  }

  clearSelectedGear(): void {
    this.form.reset({ id: null, name: null, label: null });
  }

  showVersionTooltip(): boolean {
    return this.form.get('id').disabled && (this.form.get('id').value || '').length === 0;
  }

  /** ControlValueAccessor **/
  writeValue(value: GearVersionSelection): void {
    this.form.setValue(value);
  }

  registerOnChange(fn: (value: GearVersionSelection) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    const method = isDisabled ? 'disable' : 'enable';
    this.form[method]();
  }

  validate(): ValidationErrors | null {
    const labelValue = this.form.get('label').value || '';
    const isValid = Object.values(this.gears).some(gear =>
      getNormalizedGearLabel(gear) === normalizeGearLabel(labelValue, gear.gear?.custom?.flywheel?.suite)
    );
    const validationError = { invalidLabel: true };
    // clear visual error state if label control is cleared
    this.form.get('label').setErrors(!labelValue || isValid ? null : validationError);
    return isValid ? null : validationError;
  }
}
